/**
 * AdminUsers.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useToast } from './ToastContainer';
import './AdminUsers.css';
import './AdminUsersExtensions.css';

const API_URL = '/api/users';

const AdminUsers = () => {
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]); // For bulk operations
  const [searchTerm, setSearchTerm] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportReportModal, setShowImportReportModal] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [importReportFilter, setImportReportFilter] = useState('all');
  const [importMode, setImportMode] = useState('update');
  const importFileInputRef = useRef(null);
  const [showUserActionsModal, setShowUserActionsModal] = useState(false);
  const [selectedUserForActions, setSelectedUserForActions] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityUser, setActivityUser] = useState(null);
  const [activityEvents, setActivityEvents] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showMassEmailModal, setShowMassEmailModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [products, setProducts] = useState([]);
  const [massEmailForm, setMassEmailForm] = useState({
    subject: '',
    html_content: '',
    text_content: '',
    template_id: '',
    template_variables: {},
    recipient_filters: {
      newsletter_subscribed: false,
      email_verified: false,
      role: ''
    }
  });
  const [templateForm, setTemplateForm] = useState({
    id: null,
    name: '',
    subject: '',
    html_content: '',
    text_content: '',
    variables: {}
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [newPassword, setNewPassword] = useState('');
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    isActive: true,
    emailVerified: false,
    phone: '',
    shippingAddress: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
    newsletter: false
  });
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    newsletter: false,
    sendInviteEmail: true
  });
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [sortColumn, setSortColumn] = useState('joined');
  const [sortDirection, setSortDirection] = useState('desc');

  // Helper function to close create modal and reset states
  const closeCreateModal = () => {
    setShowCreateModal(false);
    setShowCreatePassword(false);
    setShowConfirmPassword(false);
    setError(null);
  };

  // Helper function to close password modal and reset states
  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setShowNewPassword(false);
    setNewPassword('');
    setError(null);
  };

  // Fetch users with cache busting
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Not authenticated. Please log in first.');
      }
      
      const timestamp = new Date().getTime(); // Cache buster
      const response = await fetch(`${API_URL}?_t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        // Try to get error details
        let errorMessage = 'Failed to fetch users';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setUsers(data);
      setSelectedUsers([]); // Clear selection after refresh
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/roles/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      setRoles(data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchEmailTemplates();
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch products from catalog
  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products');
      if (response.ok) {
        const productsData = await response.json();
        setProducts(productsData);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Fetch email templates
  const fetchEmailTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.warn('No token found in localStorage');
        return;
      }
      
      const response = await fetch('/api/admin/email-templates', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const templates = await response.json();
        console.log('✅ Email templates loaded:', templates.length);
        setEmailTemplates(templates);
      } else if (response.status === 401) {
        console.error('Authentication failed - token may be invalid. Please log out and log back in.');
        showError('Session expired. Please log out and log back in.');
      } else {
        console.error('Failed to fetch templates:', response.status);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  // Open role management modal
  const openRoleModal = (user) => {
    setSelectedUser(user);
    setUserRoles(user.roles || []);
    setShowRoleModal(true);
  };

  // Open password reset modal
  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  // Open edit user modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    
    // Load profile data from user object (from database) FIRST
    let profileData = {
      phone: user.phone || '',
      shippingAddress: '', // Not fetched yet
      city: user.city || '',
      province: user.province || '',
      postalCode: user.postal_code || '',
      country: user.country || '',
      newsletter: user.newsletter_subscribed || false
    };
    
    // Optionally overlay with localStorage data if it exists (for fields not in DB)
    const savedProfile = localStorage.getItem(`userProfile_${user.username}`);
    if (savedProfile) {
      try {
        const parsed = JSON.parse(savedProfile);
        profileData = {
          phone: user.phone || parsed.phone || '',
          shippingAddress: parsed.shippingAddress || '',
          city: user.city || parsed.city || '',
          province: user.province || parsed.province || '',
          postalCode: user.postal_code || parsed.postalCode || '',
          country: user.country || parsed.country || '',
          newsletter: user.newsletter_subscribed !== undefined ? user.newsletter_subscribed : (parsed.newsletter || false)
        };
      } catch (error) {
        console.error('Error parsing profile data:', error);
      }
    }
    
    setEditForm({
      username: user.username || '',
      email: user.email || '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      isActive: user.is_active !== 0,
      emailVerified: user.email_verified === 1 || user.email_verified === true,
      ...profileData
    });
    setShowEditModal(true);
  };

  // Reset user password
  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/${selectedUser.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newPassword })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset password');
      }

      closePasswordModal();
      setSelectedUser(null);
      showSuccess('Password reset successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Update user details
  const updateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('📤 Sending update request for user:', selectedUser.id);
      console.log('📋 Update data (editForm):', editForm);
      
      const response = await fetch(`${API_URL}/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to update user';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          // If response is not JSON, get the text
          const responseText = await response.text();
          console.error('Non-JSON response:', responseText);
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      await response.json();

      // Save profile data to localStorage
      const profileData = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
        shippingAddress: editForm.shippingAddress,
        city: editForm.city,
        province: editForm.province,
        postalCode: editForm.postalCode,
        country: editForm.country,
        newsletter: editForm.newsletter
      };
      localStorage.setItem(`userProfile_${editForm.username}`, JSON.stringify(profileData));

      // Update local state
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { 
              ...user, 
              username: editForm.username,
              email: editForm.email,
              first_name: editForm.firstName,
              last_name: editForm.lastName,
              is_active: editForm.isActive ? 1 : 0,
              email_verified: editForm.emailVerified ? 1 : 0,
              newsletter_subscribed: editForm.newsletter
            }
          : user
      ));

      setShowEditModal(false);
      setSelectedUser(null);
      setError(null);
      showSuccess('User profile updated successfully');
    } catch (err) {
      console.error('Update user error:', err);
      setError(err.message);
    }
  };

  // Update user roles
  const updateUserRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/${selectedUser.id}/roles`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ roles: userRoles })
      });

      if (!response.ok) {
        throw new Error('Failed to update roles');
      }

      // Update local state
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { ...user, roles: userRoles }
          : user
      ));

      setShowRoleModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err.message);
    }
  };

  // Create new user
  const createUser = async () => {
    // Validation
    if (!createForm.username || !createForm.email || !createForm.password) {
      setError('Username, email, and password are required');
      return;
    }

    if (createForm.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: createForm.username,
          email: createForm.email,
          password: createForm.password,
          firstName: createForm.firstName,
          lastName: createForm.lastName,
          newsletterSubscribed: createForm.newsletter,
          sendInviteEmail: createForm.sendInviteEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user');
      }

      await response.json();

      // Refresh user list
      await fetchUsers();

      // Reset form and close modal
      setCreateForm({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        newsletter: false,
        sendInviteEmail: true
      });
      closeCreateModal();
      
      // Show appropriate success message
      if (createForm.sendInviteEmail) {
        showSuccess('User created successfully! Welcome email sent to ' + createForm.email);
      } else {
        showSuccess('User created successfully!');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Delete user
  const deleteUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }

      // Force refresh from server to clear cache
      await fetchUsers();

      setShowDeleteModal(false);
      setSelectedUser(null);
      setError(null);
      showSuccess('User deleted successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Bulk delete users
  const bulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      // Delete each user
      for (const userId of selectedUsers) {
        try {
          const response = await fetch(`${API_URL}/${userId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            successCount++;
          } else {
            const errorData = await response.json();
            failCount++;
            errors.push(`User ID ${userId}: ${errorData.error}`);
          }
        } catch (err) {
          failCount++;
          errors.push(`User ID ${userId}: ${err.message}`);
        }
      }

      // Force refresh from server to clear cache
      await fetchUsers();

      setShowBulkDeleteModal(false);
      setSelectedUsers([]);
      setError(null);

      if (failCount === 0) {
        showSuccess(`Successfully deleted ${successCount} user(s)`);
      } else {
        showError(`Deleted ${successCount} user(s). Failed to delete ${failCount} user(s). Errors: ${errors.join(', ')}`);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle row click to open actions modal
  const handleRowClick = (user, e) => {
    // Don't open modal if clicking checkbox
    if (e.target.type === 'checkbox') return;
    
    setSelectedUserForActions(user);
    setShowUserActionsModal(true);
  };

  // Handle actions from modal
  const handleQuickEdit = () => {
    setShowUserActionsModal(false);
    openEditModal(selectedUserForActions);
  };

  const handleQuickPassword = () => {
    setShowUserActionsModal(false);
    openPasswordModal(selectedUserForActions);
  };

  const handleQuickRoles = () => {
    setShowUserActionsModal(false);
    openRoleModal(selectedUserForActions);
  };

  const handleQuickDelete = () => {
    setShowUserActionsModal(false);
    openDeleteModal(selectedUserForActions);
  };

  const openActivityModal = async (user, filter = 'all') => {
    setActivityUser(user);
    setShowActivityModal(true);
    setShowUserActionsModal(false);
    setActivityFilter(filter);
    setActivityLoading(true);
    setActivityError(null);
    setActivityEvents([]);
    setActivityTotal(0);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');
      const qs = new URLSearchParams({ limit: '200' });
      if (filter && filter !== 'all') qs.set('eventType', filter);
      const response = await fetch(`${API_URL}/${user.id}/activity?${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        let msg = 'Failed to load activity';
        try {
          const err = await response.json();
          msg = err.error || msg;
        } catch (_) { /* ignore */ }
        throw new Error(msg);
      }
      const data = await response.json();
      setActivityEvents(Array.isArray(data.events) ? data.events : []);
      setActivityTotal(data.total || 0);
      if (data.user) {
        setActivityUser((prev) => ({ ...prev, ...data.user, last_seen_at: data.user.lastSeenAt }));
      }
    } catch (err) {
      setActivityError(err.message || 'Failed to load activity');
    } finally {
      setActivityLoading(false);
    }
  };

  const handleQuickActivity = () => {
    if (!selectedUserForActions) return;
    openActivityModal(selectedUserForActions, 'all');
  };

  const formatActivityMeta = (meta) => {
    if (!meta || typeof meta !== 'object') return '';
    const parts = [];
    if (meta.title) parts.push(meta.title);
    if (meta.query) parts.push(`“${meta.query}”`);
    if (meta.name) parts.push(meta.name);
    if (meta.productId != null) parts.push(`#${meta.productId}`);
    if (meta.quantity != null) parts.push(`×${meta.quantity}`);
    if (parts.length) return parts.join(' · ');
    try {
      return JSON.stringify(meta);
    } catch {
      return '';
    }
  };

  // Toggle select all (only current page)
  const toggleSelectAll = () => {
    const currentPageUserIds = paginatedUsers.map(user => user.id);
    const allCurrentPageSelected = currentPageUserIds.every(id => selectedUsers.includes(id));
    
    if (allCurrentPageSelected) {
      // Deselect all users on current page
      setSelectedUsers(prev => prev.filter(id => !currentPageUserIds.includes(id)));
    } else {
      // Select all users on current page
      setSelectedUsers(prev => [...new Set([...prev, ...currentPageUserIds])]);
    }
  };

  const roleByName = useMemo(() => {
    const m = new Map();
    (roles || []).forEach((r) => {
      m.set(r.name, {
        level: r.level != null ? Number(r.level) : 0,
        display_name: r.display_name || r.name.replace(/_/g, ' '),
      });
    });
    return m;
  }, [roles]);

  const getHighestRoleForUser = useCallback(
    (user) => {
      const names = user.roles || [];
      if (!names.length) {
        return { name: null, label: '—', level: -1 };
      }
      let bestName = names[0];
      let bestLevel = roleByName.get(bestName)?.level ?? 0;
      for (let i = 1; i < names.length; i += 1) {
        const n = names[i];
        const lv = roleByName.get(n)?.level ?? 0;
        if (lv > bestLevel) {
          bestLevel = lv;
          bestName = n;
        }
      }
      const meta = roleByName.get(bestName);
      return {
        name: bestName,
        label: meta?.display_name || bestName.replace(/_/g, ' '),
        level: meta != null ? meta.level : bestLevel,
      };
    },
    [roleByName],
  );

  // Filter users based on search term (searches across all profile information)
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;

    const search = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.username?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        user.first_name?.toLowerCase().includes(search) ||
        user.last_name?.toLowerCase().includes(search) ||
        user.phone?.toLowerCase().includes(search) ||
        user.city?.toLowerCase().includes(search) ||
        user.postal_code?.toLowerCase().includes(search) ||
        user.province?.toLowerCase().includes(search) ||
        user.country?.toLowerCase().includes(search) ||
        user.company_name?.toLowerCase().includes(search) ||
        user.roles?.some((role) => role.toLowerCase().includes(search)),
    );
  }, [users, searchTerm]);

  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers];
    const dir = sortDirection === 'asc' ? 1 : -1;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

    list.sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case 'username':
          cmp = collator.compare(a.username || '', b.username || '');
          break;
        case 'last_name':
          cmp = collator.compare(a.last_name || '', b.last_name || '');
          break;
        case 'email':
          cmp = collator.compare(a.email || '', b.email || '');
          break;
        case 'phone':
          cmp = collator.compare(a.phone || '', b.phone || '');
          break;
        case 'city':
          cmp = collator.compare(a.city || '', b.city || '');
          break;
        case 'role': {
          const ha = getHighestRoleForUser(a);
          const hb = getHighestRoleForUser(b);
          cmp = ha.level - hb.level;
          if (cmp === 0) cmp = collator.compare(ha.label, hb.label);
          break;
        }
        case 'status': {
          const sa = (a.is_active ? 2 : 0) + (a.email_verified ? 1 : 0);
          const sb = (b.is_active ? 2 : 0) + (b.email_verified ? 1 : 0);
          cmp = sa - sb;
          if (cmp === 0) cmp = (a.newsletter_subscribed ? 1 : 0) - (b.newsletter_subscribed ? 1 : 0);
          break;
        }
        case 'joined':
          cmp =
            (new Date(a.created_at).getTime() || 0) -
            (new Date(b.created_at).getTime() || 0);
          break;
        case 'last_seen':
          cmp =
            (new Date(a.last_seen_at || 0).getTime() || 0) -
            (new Date(b.last_seen_at || 0).getTime() || 0);
          break;
        default:
          return 0;
      }
      if (cmp === 0) return 0;
      return cmp * dir;
    });
    return list;
  }, [filteredUsers, sortColumn, sortDirection, getHighestRoleForUser]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(column === 'joined' ? 'desc' : 'asc');
    }
  };

  const renderSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Pagination calculations
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortColumn, sortDirection]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Toggle role selection
  const toggleRole = (roleName) => {
    setUserRoles(prev => 
      prev.includes(roleName)
        ? prev.filter(role => role !== roleName)
        : [...prev, roleName]
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return String(dateString);
    }
  };

  // Handle template selection
  const handleTemplateSelect = async (templateId) => {
    if (!templateId) {
      setMassEmailForm(prev => ({ ...prev, template_id: '' }));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/email-templates/${templateId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const template = await response.json();
        
        // Parse template variables
        let templateVars = {};
        try {
          templateVars = typeof template.variables === 'string' 
            ? JSON.parse(template.variables) 
            : (template.variables || {});
        } catch (e) {
          console.error('Error parsing template variables:', e);
        }
        
        setMassEmailForm(prev => ({
          ...prev,
          template_id: templateId,
          subject: template.subject,
          html_content: template.html_content,
          text_content: template.text_content || '',
          template_variables: templateVars
        }));
        setSelectedTemplate(template);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      showError('Failed to load template');
    }
  };

  // Handle template variable changes
  const handleTemplateVariableChange = (key, value) => {
    setMassEmailForm(prev => ({
      ...prev,
      template_variables: {
        ...prev.template_variables,
        [key]: value
      }
    }));
  };

  // Handle product selection for New Product Release template
  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === parseInt(productId));
    if (product) {
      // Build product URL - uses environment variable or window.location.origin
      // Format: https://cranfordpub.ca/product/628 (or relative /product/628)
      // For emails, we need absolute URLs, so use window.location.origin
      // Check for permalink first, then fall back to ID
      const baseUrl = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_FRONTEND_URL || window.location.origin;
      const productUrl = product.permalink 
        ? `${baseUrl}/product/${product.permalink}`
        : product.id 
        ? `${baseUrl}/product/${product.id}`
        : `${baseUrl}/shop`;
      
      // Format price
      const productPrice =
        product.price_cents != null && product.price_cents !== ''
          ? `$${(Number(product.price_cents) / 100).toFixed(2)}`
          : product.price != null && product.price !== ''
            ? `$${parseFloat(product.price).toFixed(2)}`
            : '';
      
      // Get product image URL - handle various image formats
      // For emails, we need absolute URLs
      // Use environment variable or window.location.origin for base URL
      const imageBaseUrl = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_FRONTEND_URL || window.location.origin;
      let productImageUrl = '';
      if (product.image) {
        if (product.image.startsWith('http://') || product.image.startsWith('https://')) {
          productImageUrl = product.image;
        } else if (product.image.startsWith('/api/assets/')) {
          // Handle /api/assets/{id}/download format
          if (product.image.includes('/download')) {
            const assetId = product.image.split('/')[3];
            productImageUrl = `${imageBaseUrl}/api/assets/${assetId}/thumbnail`;
          } else {
            // Already a full API path
            productImageUrl = `${imageBaseUrl}${product.image}`;
          }
        } else if (product.image.startsWith('/')) {
          productImageUrl = `${imageBaseUrl}${product.image}`;
        } else if (product.image.includes('product_images_consolidated/')) {
          productImageUrl = `${imageBaseUrl}/images/products/${product.image.replace('product_images_consolidated/', '')}`;
        } else if (product.image.match(/^\d{13}-[a-z0-9]+-/) || 
                   product.image.match(/^file-\d+-[a-z0-9]+\./) ||
                   product.image.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/) ||
                   product.image.includes('uploads/')) {
          // Database asset format - try to extract asset ID or use filename
          // For now, use the filename directly with thumbnail endpoint
          productImageUrl = `${imageBaseUrl}/api/assets/${product.image}/thumbnail`;
        } else {
          // Default to products folder
          productImageUrl = `${imageBaseUrl}/images/products/${product.image}`;
        }
      } else {
        // If no image, use placeholder
        productImageUrl = `${imageBaseUrl}/images/placeholder.jpg`;
      }
      
      // Auto-populate product-related fields
      const updatedVars = {
        ...massEmailForm.template_variables,
        product_name: product.name || '',
        product_description: product.description || '',
        product_price: productPrice,
        product_image_url: productImageUrl,
        product_url: productUrl,
        product_sku: product.sku || '',
        feature_1: product.features?.[0] || product.description?.split('.')[0] || '',
        feature_2: product.features?.[1] || product.description?.split('.')[1] || '',
        feature_3: product.features?.[2] || product.description?.split('.')[2] || ''
      };
      
      setMassEmailForm(prev => ({
        ...prev,
        template_variables: updatedVars
      }));
    }
  };
  
  // Get friendly field labels for template variables
  const getFieldLabel = (key) => {
    const labels = {
      product_name: 'Product Name',
      out_of_stock_message: 'Out of Stock Message',
      option_1: 'Option 1',
      option_2: 'Option 2',
      option_3: 'Option 3',
      order_number: 'Order Number',
      refund_amount: 'Refund Amount',
      refund_date: 'Refund Date',
      payment_method: 'Payment Method',
      refund_message: 'Refund Message',
      expectation_1: 'Expectation 1',
      expectation_2: 'Expectation 2',
      expectation_3: 'Expectation 3',
      old_shipping_cost: 'Old Shipping Cost',
      new_shipping_cost: 'New Shipping Cost',
      shipping_difference: 'Shipping Difference',
      shipping_message: 'Shipping Message',
      announcement_title: 'Announcement Title',
      announcement_subtitle: 'Announcement Subtitle',
      announcement_intro: 'Introduction',
      announcement_content: 'Main Content',
      newsletter_title: 'Newsletter Title',
      newsletter_content: 'Newsletter Content',
      featured_content: 'Featured Content',
      event_name: 'Event Name',
      event_date: 'Event Date',
      event_time: 'Event Time',
      event_location: 'Event Location',
      event_description: 'Event Description',
      event_registration_url: 'Registration URL',
      discount_percent: 'Discount Percentage',
      offer_title: 'Offer Title',
      offer_description: 'Offer Description',
      promo_code: 'Promo Code',
      expiry_date: 'Expiry Date',
      key_point_1: 'Key Point 1',
      key_point_2: 'Key Point 2',
      key_point_3: 'Key Point 3',
      cta_text: 'Call-to-Action Text',
      cta_url: 'Call-to-Action URL',
      additional_info: 'Additional Information',
      feature_1: 'Feature 1',
      feature_2: 'Feature 2',
      feature_3: 'Feature 3',
      product_description: 'Product Description',
      product_price: 'Product Price',
      product_image_url: 'Product Image URL',
      product_url: 'Product Purchase Link',
      product_sku: 'Product SKU'
    };
    return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Send mass email
  const handleSendMassEmail = async () => {
    if (!massEmailForm.subject || !massEmailForm.html_content) {
      showError('Subject and HTML content are required');
      return;
    }

    setIsSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const recipientIds = selectedUsers.length > 0 ? selectedUsers : null;
      
      const response = await fetch('/api/admin/mass-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...massEmailForm,
          recipient_ids: recipientIds
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showSuccess(`Email sent to ${result.sent} recipient(s). ${result.failed > 0 ? `${result.failed} failed.` : ''}`);
        setShowMassEmailModal(false);
        setMassEmailForm({
          subject: '',
          html_content: '',
          text_content: '',
          template_id: '',
          template_variables: {},
          recipient_filters: {
            newsletter_subscribed: false,
            email_verified: false,
            role: ''
          }
        });
        setSelectedTemplate(null);
      } else {
        showError(result.error || 'Failed to send mass email');
      }
    } catch (error) {
      console.error('Error sending mass email:', error);
      showError('Failed to send mass email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Save template
  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.html_content) {
      showError('Name, subject, and HTML content are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/email-templates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateForm)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showSuccess('Template saved successfully');
        await fetchEmailTemplates();
        setShowTemplateModal(false);
        setTemplateForm({
          id: null,
          name: '',
          subject: '',
          html_content: '',
          text_content: '',
          variables: {}
        });
      } else {
        showError(result.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save template');
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/email-templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showSuccess('Template deleted successfully');
        await fetchEmailTemplates();
        if (selectedTemplate && selectedTemplate.id === templateId) {
          setSelectedTemplate(null);
          setMassEmailForm(prev => ({ ...prev, template_id: '' }));
        }
      } else {
        showError('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      showError('Failed to delete template');
    }
  };

  // Load template for editing
  const handleEditTemplate = (template) => {
    setTemplateForm({
      id: template.id,
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      text_content: template.text_content || '',
      variables: template.variables || {}
    });
    setShowTemplateModal(true);
  };

  // Insert example templates
  const handleInsertExampleTemplates = async () => {
    if (!window.confirm('This will insert 5 example email templates. Existing templates with the same names will be updated. Continue?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/email-templates/insert-examples', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showSuccess(result.message || 'Example templates inserted successfully!');
        await fetchEmailTemplates();
      } else {
        showError(result.error || 'Failed to insert example templates');
      }
    } catch (error) {
      console.error('Error inserting example templates:', error);
      showError('Failed to insert example templates');
    }
  };

  // Test template
  const handleTestTemplate = async (templateId) => {
    const testEmail = prompt('Enter test email address:');
    if (!testEmail) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/email-templates/${templateId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ test_email: testEmail })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showSuccess('Test email sent successfully');
      } else {
        showError(result.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      showError('Failed to send test email');
    }
  };

  // Export users to CSV
  const downloadCSV = async () => {
    try {
      const response = await fetch(`${API_URL}/export/csv`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSuccess('Users exported successfully');
    } catch (error) {
      console.error('Error exporting users:', error);
      showError('Failed to export users');
    }
  };

  // Handle import modal
  const handleImportClick = () => {
    setShowImportModal(true);
  };

  const handleImportConfirm = () => {
    setShowImportModal(false);
    importFileInputRef.current.click();
  };

  const downloadLegacySampleImportCSV = () => {
    const csvContent = [
      'Order #,Billing Name,Total,Shipping Email,Shipping Phone',
      '7133,Markus Kempen,0.02,mvk4ibm@gmail.com,4164366235',
      '7134,Markus Kempen,0.01,mvk4ibm@gmail.com,++1 416-436-6235',
      '',
      '# Order # and Total are ignored.',
      '# Duplicate emails in the file are merged into one account (unique by email).',
      '# Phone numbers are normalized (e.g. ++1 416-436-6235 → 416-436-6235).',
      '# Imported users must reset their password via Forgot Password.',
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'legacy_customers_import_sample.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    showSuccess('Legacy import sample CSV downloaded');
  };

  const formatImportResultMessage = (result) => {
    if (result.summary) {
      const s = result.summary;
      return result.message || `Import finished: ${s.created} created, ${s.updated} updated, ${s.failed} failed.`;
    }
    const parts = [result.message || 'Import completed'];
    if (result.format === 'legacy_orders') {
      parts.push('Legacy format detected.');
    }
    if (result.imported != null) {
      parts.push(`${result.imported} user(s) imported.`);
    }
    if (result.duplicateRowsSkipped > 0) {
      parts.push(`${result.duplicateRowsSkipped} duplicate email row(s) merged.`);
    }
    if (result.invalidRowsSkipped > 0) {
      parts.push(`${result.invalidRowsSkipped} invalid row(s) skipped.`);
    }
    return parts.join(' ');
  };

  const importReportActionLabel = (action) => {
    const labels = {
      created: 'Created',
      updated: 'Updated',
      failed: 'Failed',
      skipped_invalid: 'Skipped (invalid)',
      merged_duplicate: 'Merged duplicate',
      mode_replace: 'Replace mode',
    };
    return labels[action] || action;
  };

  const importReportActionClass = (action) => {
    const classes = {
      created: 'import-report-action--created',
      updated: 'import-report-action--updated',
      failed: 'import-report-action--failed',
      skipped_invalid: 'import-report-action--skipped',
      merged_duplicate: 'import-report-action--merged',
      mode_replace: 'import-report-action--info',
    };
    return classes[action] || '';
  };

  const downloadImportReport = (reportCsv, completedAt) => {
    const stamp = (completedAt || new Date().toISOString()).replace(/[:.]/g, '-').slice(0, 19);
    const blob = new Blob([reportCsv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user_import_report_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const filteredImportReportEntries = useMemo(() => {
    if (!importReport?.report) return [];
    if (importReportFilter === 'all') return importReport.report;
    if (importReportFilter === 'issues') {
      return importReport.report.filter((e) =>
        ['failed', 'skipped_invalid', 'merged_duplicate'].includes(e.action)
      );
    }
    return importReport.report.filter((e) => e.action === importReportFilter);
  }, [importReport, importReportFilter]);

  const showImportReport = (result) => {
    if (!result?.summary && !result?.report) return;
    setImportReport({
      summary: result.summary || {},
      report: result.report || [],
      reportCsv: result.reportCsv || '',
      message: result.message || result.error || '',
      success: result.success !== false && !result.error,
    });
    setImportReportFilter('all');
    setShowImportReportModal(true);
  };

  const downloadStandardSampleImportCSV = () => {
    const header = [
      'id', 'username', 'email', 'password_hash', 'is_active', 'email_verified',
      'first_name', 'last_name', 'phone', 'address_line1', 'address_line2', 'city', 'state_province',
      'postal_code', 'country', 'newsletter_subscribed', 'roles', 'created_at',
    ].join(',');

    const sampleRow = [
      '',
      '"jsmith"',
      '"jsmith@example.com"',
      '""',
      '1',
      '1',
      '"Jane"',
      '"Smith"',
      '"9025550100"',
      '"12 Main Street"',
      '""',
      '"Sydney"',
      '"NS"',
      '"B1P 1A1"',
      '"Canada"',
      '1',
      '"customer"',
      `"${new Date().toISOString()}"`,
    ].join(',');

    const csvContent = [
      header,
      sampleRow,
      '',
      '# Leave password_hash empty for legacy imports — customers reset via Forgot Password.',
      '# Remove id for new users. Export existing users first to match column order.',
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users_import_sample.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    showSuccess('Sample import CSV downloaded');
  };

  // Handle CSV upload
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csvData = event.target.result;
        const response = await fetch(`${API_URL}/import/csv`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            csvData: csvData,
            mode: importMode
          })
        });
        
        const result = await response.json();

        if (result.summary || result.report) {
          showImportReport(result);
        }

        if (!response.ok) {
          throw new Error(result.error || 'Import failed');
        }

        if (result.success === false || (result.summary && result.summary.failed > 0)) {
          showError(formatImportResultMessage(result));
        } else {
          showSuccess(formatImportResultMessage(result));
        }

        fetchUsers();
      } catch (error) {
        console.error('Error importing users:', error);
        showError(error.message || 'Failed to import users');
      }
      
      // Reset file input
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  // Get role badge color (brighter for text-only display)
  const getRoleBadgeColor = (role) => {
    const colors = {
      'super_admin': '#ff5555',
      'admin': '#50e3c2',
      'content_creator': '#4fc3f7',
      'wholesaler': '#81c784',
      'customer': '#ffd54f'
    };
    return colors[role] || '#90caf9';
  };

  if (loading) {
    return (
      <div className="admin-users-container">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-users-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="admin-users-container">
      {/* Compact Header */}
      <div className="admin-header-compact">
        <h2>👥 Users</h2>
        <div className="header-stats">
          <span className="stat-inline" title="Total Users">📊 {users.length}</span>
          {searchTerm && (
            <span className="stat-inline" title="Filtered Results">🔍 {sortedUsers.length}</span>
          )}
          <span className="stat-inline" title="Page">{totalPages > 0 ? `📄 ${currentPage}/${totalPages}` : '📄 0/0'}</span>
        </div>
        <div className="header-actions">
          <button 
            className="create-user-btn"
            onClick={() => setShowCreateModal(true)}
          >
            ➕ New
          </button>
          <div className="btn-group">
            <button 
              className="btn btn-sm btn-info" 
              onClick={downloadCSV}
              title="Export all users to CSV"
            >
              📥 Export
            </button>
            <button 
              className="btn btn-sm btn-info" 
              onClick={handleImportClick}
              title="Import users from CSV"
            >
              📤 Import
            </button>
            <button 
              className="btn btn-sm btn-primary" 
              onClick={() => setShowMassEmailModal(true)}
              title="Send mass email to users"
            >
              📧 Mass Email
            </button>
            <button 
              className="btn btn-sm btn-secondary" 
              onClick={() => {
                setSelectedTemplate(null);
                setTemplateForm({
                  id: null,
                  name: '',
                  subject: '',
                  html_content: '',
                  text_content: '',
                  variables: {}
                });
                setShowTemplateModal(true);
              }}
              title="Manage email templates"
            >
              📝 Templates
            </button>
          </div>
        </div>
      </div>

      {/* Compact Search and Bulk Actions */}
      <div className="toolbar-compact">
        <div className="search-box-compact">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by username, name, email, phone, city, zip..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {selectedUsers.length > 0 && (
          <div className="bulk-actions-compact">
            <span className="selected-count">
              {selectedUsers.length} selected
            </span>
            <button 
              className="bulk-delete-btn"
              onClick={() => setShowBulkDeleteModal(true)}
              title={`Delete ${selectedUsers.length} user(s)`}
            >
              🗑️
            </button>
            <button 
              className="deselect-all-btn"
              onClick={() => setSelectedUsers([])}
              title="Clear selection"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th className="checkbox-column">
                <input
                  type="checkbox"
                  checked={paginatedUsers.length > 0 && paginatedUsers.every(user => selectedUsers.includes(user.id))}
                  onChange={toggleSelectAll}
                  title="Select all on this page"
                />
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('username')}
                scope="col"
                title="Sort by username"
              >
                Username{renderSortIndicator('username')}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('last_name')}
                scope="col"
                title="Sort by last name"
              >
                Last name{renderSortIndicator('last_name')}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('email')}
                scope="col"
                title="Sort by email"
              >
                Email{renderSortIndicator('email')}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('phone')}
                scope="col"
                title="Sort by phone"
              >
                Phone{renderSortIndicator('phone')}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('city')}
                scope="col"
                title="Sort by city"
              >
                City{renderSortIndicator('city')}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('role')}
                scope="col"
                title="Sort by highest role (level)"
              >
                Role{renderSortIndicator('role')}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('status')}
                scope="col"
                title="Sort by status"
              >
                Status{renderSortIndicator('status')}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('joined')}
                scope="col"
                title="Sort by join date"
              >
                Joined{renderSortIndicator('joined')}
              </th>
              <th
                className="sortable"
                onClick={() => handleSort('last_seen')}
                scope="col"
                title="Sort by last activity"
              >
                Last seen{renderSortIndicator('last_seen')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-results">
                  {searchTerm ? `No users found matching "${searchTerm}"` : 'No users found'}
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => {
                const highest = getHighestRoleForUser(user);
                const allRolesTitle =
                  user.roles?.length > 1
                    ? `All assigned roles: ${user.roles.map((r) => r.replace(/_/g, ' ')).join(', ')}`
                    : undefined;
                return (
                <tr 
                  key={user.id}
                  className={selectedUsers.includes(user.id) ? 'selected-row' : ''}
                  onClick={(e) => handleRowClick(user, e)}
                >
                  <td className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      disabled={user.source === 'demo'}
                      title={user.source === 'demo' ? 'Demo users cannot be deleted' : 'Select user'}
                    />
                  </td>
                  <td className="user-username" title={user.username ? `@${user.username}` : ''}>
                    {user.username || '—'}
                  </td>
                  <td className="user-lastname">
                    {user.last_name || '—'}
                  </td>
                  <td className="user-email">{user.email}</td>
                  <td className="user-phone" title={`Phone: ${user.phone || 'Not set'}`}>
                    {user.phone || '-'}
                  </td>
                  <td className="user-city" title={`City: ${user.city || 'Not set'}`}>
                    {user.city || '-'}
                  </td>
                <td>
                  <div className="roles-container">
                    {highest.name ? (
                      <span
                        className="role-badge"
                        style={{ color: getRoleBadgeColor(highest.name) }}
                        title={allRolesTitle}
                      >
                        {highest.label}
                      </span>
                    ) : (
                      <span className="role-badge muted">—</span>
                    )}
                  </div>
                </td>
                <td className="user-status">
                  <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                    <span 
                      className={`status-indicator ${user.is_active ? 'active' : 'inactive'}`}
                      title={user.is_active ? 'Active' : 'Inactive'}
                    >
                      {user.is_active ? '🟢' : '🔴'}
                    </span>
                    <span 
                      className={`newsletter-status ${user.newsletter_subscribed ? 'subscribed' : 'not-subscribed'}`}
                      title={user.newsletter_subscribed ? 'Newsletter ✓' : 'Newsletter ✗'}
                    >
                      📧{user.newsletter_subscribed ? '✓' : '✗'}
                    </span>
                    <span 
                      className={`email-verified-status ${user.email_verified ? 'verified' : 'not-verified'}`}
                      title={user.email_verified ? 'Email ✓' : 'Email ✗'}
                    >
                      ✉️{user.email_verified ? '✓' : '✗'}
                    </span>
                    {user.order_count > 0 && (
                      <span 
                        title={`${user.order_count} orders`}
                        style={{ 
                          fontSize: '0.75rem',
                          cursor: 'help'
                        }}
                      >
                        🛒
                      </span>
                    )}
                  </div>
                </td>
                <td>{formatDate(user.created_at)}</td>
                <td>
                  <button
                    type="button"
                    className="linkish-btn"
                    title="View activity"
                    onClick={(e) => {
                      e.stopPropagation();
                      openActivityModal(user);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: user.last_seen_at ? '#8ec8ff' : '#888',
                      cursor: 'pointer',
                      padding: 0,
                      font: 'inherit',
                      textDecoration: user.last_seen_at ? 'underline' : 'none',
                    }}
                  >
                    {user.last_seen_at ? formatDateTime(user.last_seen_at) : 'No activity'}
                  </button>
                </td>
              </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {sortedUsers.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {startIndex + 1}-{Math.min(endIndex, sortedUsers.length)} of {sortedUsers.length} users
          </div>
          
          <div className="pagination-controls">
            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              ⟪
            </button>
            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              title="Previous page"
            >
              ‹
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              ›
            </button>
            <button 
              className="pagination-btn"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              ⟫
            </button>
          </div>

          <div className="pagination-size">
            <label htmlFor="itemsPerPage">Per page:</label>
            <select 
              id="itemsPerPage"
              value={itemsPerPage} 
              onChange={handleItemsPerPageChange}
              className="items-per-page-select"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      )}

      {/* Role Management Modal */}
      {showRoleModal && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Roles for {selectedUser?.username}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowRoleModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="current-roles">
                <h4>Current Roles:</h4>
                <div className="selected-roles">
                  {userRoles.map((role, index) => (
                    <span key={`current-${role}-${index}`} className="selected-role">
                      {role.replace('_', ' ')}
                      <button onClick={() => toggleRole(role)}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="available-roles">
                <h4>Available Roles:</h4>
                <div className="roles-grid">
                  {roles.map(role => (
                    <label key={role.id} className="role-option">
                      <input
                        type="checkbox"
                        checked={userRoles.includes(role.name)}
                        onChange={() => toggleRole(role.name)}
                      />
                      <span className="role-name">{role.display_name}</span>
                      <span className="role-level">Level {role.level}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => setShowRoleModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={updateUserRoles}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={closePasswordModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password for {selectedUser?.username}</h3>
              <button 
                className="close-btn"
                onClick={closePasswordModal}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="form-input"
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '18px',
                      color: '#666',
                      padding: '5px'
                    }}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>
              <div className="warning-message">
                ⚠️ This will immediately change the user's password. They will need to use this new password to log in.
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={closePasswordModal}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={resetPassword}
                disabled={!newPassword || newPassword.length < 6}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit User: {selectedUser?.username}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="profile-section-header">
                <h4>👤 Account Information</h4>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="editUsername">Username *</label>
                  <input
                    type="text"
                    id="editUsername"
                    value={editForm.username}
                    onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editEmail">Email *</label>
                  <input
                    type="email"
                    id="editEmail"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editFirstName">First Name</label>
                  <input
                    type="text"
                    id="editFirstName"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editLastName">Last Name</label>
                  <input
                    type="text"
                    id="editLastName"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editPhone">Phone Number</label>
                  <input
                    type="tel"
                    id="editPhone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="form-input"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="profile-section-header">
                <h4>📍 Shipping Address</h4>
              </div>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label htmlFor="editAddress">Street Address</label>
                  <input
                    type="text"
                    id="editAddress"
                    value={editForm.shippingAddress}
                    onChange={(e) => setEditForm(prev => ({ ...prev, shippingAddress: e.target.value }))}
                    className="form-input"
                    placeholder="123 Main St"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editCity">City</label>
                  <input
                    type="text"
                    id="editCity"
                    value={editForm.city}
                    onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                    className="form-input"
                    placeholder="Halifax"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editProvince">Province/State</label>
                  <input
                    type="text"
                    id="editProvince"
                    value={editForm.province}
                    onChange={(e) => setEditForm(prev => ({ ...prev, province: e.target.value }))}
                    className="form-input"
                    placeholder="Nova Scotia"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editPostalCode">Postal Code</label>
                  <input
                    type="text"
                    id="editPostalCode"
                    value={editForm.postalCode}
                    onChange={(e) => setEditForm(prev => ({ ...prev, postalCode: e.target.value }))}
                    className="form-input"
                    placeholder="B3H 4R2"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editCountry">Country</label>
                  <input
                    type="text"
                    id="editCountry"
                    value={editForm.country}
                    onChange={(e) => setEditForm(prev => ({ ...prev, country: e.target.value }))}
                    className="form-input"
                    placeholder="Canada"
                  />
                </div>
              </div>

              <div className="profile-section-header">
                <h4>⚙️ Preferences</h4>
              </div>
              <div className="form-checkboxes">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editForm.newsletter}
                      onChange={(e) => setEditForm(prev => ({ ...prev, newsletter: e.target.checked }))}
                    />
                    <span>📧 Subscribe to Newsletter</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                    <span>✅ Active User Account</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editForm.emailVerified}
                      onChange={(e) => setEditForm(prev => ({ ...prev, emailVerified: e.target.checked }))}
                    />
                    <span>✉️ Email Verified</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={updateUser}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Create New User</h3>
              <button 
                className="close-btn"
                onClick={closeCreateModal}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {error && <div className="error-message">❌ {error}</div>}
              
              <div className="profile-section-header">
                <h4>👤 Account Information</h4>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="createUsername">Username *</label>
                  <input
                    type="text"
                    id="createUsername"
                    value={createForm.username}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, username: e.target.value }))}
                    className="form-input"
                    placeholder="johndoe"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="createEmail">Email *</label>
                  <input
                    type="email"
                    id="createEmail"
                    value={createForm.email}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                    className="form-input"
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="createFirstName">First Name</label>
                  <input
                    type="text"
                    id="createFirstName"
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
                    className="form-input"
                    placeholder="John"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="createLastName">Last Name</label>
                  <input
                    type="text"
                    id="createLastName"
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
                    className="form-input"
                    placeholder="Doe"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="createPassword">Password *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showCreatePassword ? "text" : "password"}
                      id="createPassword"
                      value={createForm.password}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                      className="form-input"
                      style={{ paddingRight: '40px' }}
                      placeholder="Min 6 characters"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCreatePassword(!showCreatePassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#666',
                        padding: '5px'
                      }}
                      aria-label={showCreatePassword ? "Hide password" : "Show password"}
                    >
                      {showCreatePassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="createConfirmPassword">Confirm Password *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="createConfirmPassword"
                      value={createForm.confirmPassword}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="form-input"
                      style={{ paddingRight: '40px' }}
                      placeholder="Retype password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#666',
                        padding: '5px'
                      }}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-checkboxes">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={createForm.newsletter}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, newsletter: e.target.checked }))}
                    />
                    <span>📧 Subscribe to Newsletter</span>
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={createForm.sendInviteEmail}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, sendInviteEmail: e.target.checked }))}
                    />
                    <span>📬 Send Welcome Email with Account Details</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={closeCreateModal}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={createUser}
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Delete User</h3>
              <button 
                className="close-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="delete-warning">
                <p><strong>Are you sure you want to delete this user?</strong></p>
                <div className="user-to-delete">
                  <p>👤 <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong></p>
                  <p>📧 {selectedUser?.email}</p>
                  <p>@{selectedUser?.username}</p>
                </div>
                <p className="warning-text">
                  ⚠️ This action cannot be undone. All user data will be permanently deleted.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-btn"
                onClick={deleteUser}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowBulkDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Delete Multiple Users</h3>
              <button 
                className="close-btn"
                onClick={() => setShowBulkDeleteModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="delete-warning">
                <p><strong>Are you sure you want to delete {selectedUsers.length} user(s)?</strong></p>
                <div className="users-to-delete">
                  {selectedUsers.map(userId => {
                    const user = users.find(u => u.id === userId);
                    return user ? (
                      <div key={userId} className="user-item">
                        <span>👤 {user.first_name} {user.last_name || user.username}</span>
                        <span className="user-email">📧 {user.email}</span>
                      </div>
                    ) : null;
                  })}
                </div>
                <p className="warning-text">
                  ⚠️ This action cannot be undone. All user data will be permanently deleted.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => setShowBulkDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-btn"
                onClick={bulkDeleteUsers}
              >
                Delete {selectedUsers.length} User(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Users Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content users-import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📤 Import Users</h3>
              <button className="close-btn" onClick={() => setShowImportModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <p className="users-import-intro">
                Upload a legacy order-export CSV or a full user export. Accounts are unique by
                <strong> email</strong>. Order # and Total are ignored on legacy files. Imported customers
                should use <strong>Forgot Password</strong> on first login.
              </p>
              
              <div className="form-group users-import-mode-group">
                <label className="users-import-mode-label">Import Mode:</label>
                <label className="users-import-radio">
                  <input
                    type="radio"
                    value="update"
                    checked={importMode === 'update'}
                    onChange={(e) => setImportMode(e.target.value)}
                  />
                  <span>
                    <strong>Update Mode:</strong> Update existing users and create new ones
                  </span>
                </label>
                <label className="users-import-radio">
                  <input
                    type="radio"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={(e) => setImportMode(e.target.value)}
                  />
                  <span>
                    <strong>Replace Mode:</strong> Deactivate all existing users (except super_admins) and import from CSV
                  </span>
                </label>
              </div>
              
              <div className="users-import-format-box">
                <strong className="users-import-format-title">Legacy order export (from old store)</strong>
                <p>
                  <strong>Columns:</strong> Order #, Billing Name, Total, Shipping Email, Shipping Phone
                </p>
                <p>
                  Duplicate emails are merged. Phones are normalized (e.g. <code>++1 416-436-6235</code> → <code>416-436-6235</code>).
                </p>
                <button
                  type="button"
                  className="users-import-sample-btn"
                  onClick={downloadLegacySampleImportCSV}
                >
                  Download legacy sample CSV
                </button>
              </div>

              <div className="users-import-format-box users-import-format-box--secondary">
                <strong className="users-import-format-title">Full user export format</strong>
                <p>
                  <strong>Required:</strong> username, email, password_hash, is_active, email_verified
                </p>
                <p>
                  <strong>Optional:</strong> id, first_name, last_name, phone, address fields, newsletter_subscribed, roles, created_at
                </p>
                <button
                  type="button"
                  className="users-import-sample-btn"
                  onClick={downloadStandardSampleImportCSV}
                >
                  Download standard sample CSV
                </button>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => setShowImportModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={handleImportConfirm}
              >
                Select CSV File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for CSV import */}
      <input
        type="file"
        ref={importFileInputRef}
        style={{ display: 'none' }}
        accept=".csv"
        onChange={handleCSVUpload}
      />

      {/* Import Report Modal */}
      {showImportReportModal && importReport && (
        <div className="modal-overlay" onClick={() => setShowImportReportModal(false)}>
          <div
            className="modal-content users-import-report-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>📋 User Import Report</h3>
              <button className="close-btn" onClick={() => setShowImportReportModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <p className={`users-import-report-status ${importReport.success ? 'users-import-report-status--ok' : 'users-import-report-status--warn'}`}>
                {importReport.message}
              </p>

              {importReport.summary && (
                <div className="users-import-report-summary">
                  <div className="users-import-report-stat">
                    <span className="users-import-report-stat-value">{importReport.summary.totalSourceRows ?? '—'}</span>
                    <span className="users-import-report-stat-label">Source rows</span>
                  </div>
                  <div className="users-import-report-stat users-import-report-stat--created">
                    <span className="users-import-report-stat-value">{importReport.summary.created ?? 0}</span>
                    <span className="users-import-report-stat-label">Created</span>
                  </div>
                  <div className="users-import-report-stat users-import-report-stat--updated">
                    <span className="users-import-report-stat-value">{importReport.summary.updated ?? 0}</span>
                    <span className="users-import-report-stat-label">Updated</span>
                  </div>
                  <div className="users-import-report-stat users-import-report-stat--failed">
                    <span className="users-import-report-stat-value">{importReport.summary.failed ?? 0}</span>
                    <span className="users-import-report-stat-label">Failed</span>
                  </div>
                  <div className="users-import-report-stat">
                    <span className="users-import-report-stat-value">{importReport.summary.duplicateRowsMerged ?? 0}</span>
                    <span className="users-import-report-stat-label">Duplicates merged</span>
                  </div>
                  <div className="users-import-report-stat">
                    <span className="users-import-report-stat-value">{importReport.summary.invalidRowsSkipped ?? 0}</span>
                    <span className="users-import-report-stat-label">Invalid skipped</span>
                  </div>
                </div>
              )}

              <div className="users-import-report-meta">
                {importReport.summary?.format && (
                  <span>Format: <strong>{importReport.summary.format === 'legacy_orders' ? 'Legacy order export' : importReport.summary.format}</strong></span>
                )}
                {importReport.summary?.mode && (
                  <span>Mode: <strong>{importReport.summary.mode}</strong></span>
                )}
                {importReport.summary?.completedAt && (
                  <span>Completed: <strong>{new Date(importReport.summary.completedAt).toLocaleString()}</strong></span>
                )}
              </div>

              <div className="users-import-report-filters">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'created', label: 'Created' },
                  { id: 'updated', label: 'Updated' },
                  { id: 'failed', label: 'Failed' },
                  { id: 'issues', label: 'Issues only' },
                ].map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    className={`users-import-report-filter-btn ${importReportFilter === filter.id ? 'active' : ''}`}
                    onClick={() => setImportReportFilter(filter.id)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              <div className="users-import-report-table-wrap">
                <table className="users-import-report-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Action</th>
                      <th>Email</th>
                      <th>Username</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredImportReportEntries.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="users-import-report-empty">No entries for this filter.</td>
                      </tr>
                    ) : (
                      filteredImportReportEntries.map((entry, idx) => (
                        <tr key={`${entry.source_row}-${entry.action}-${entry.email}-${idx}`}>
                          <td>{entry.source_row || '—'}</td>
                          <td>
                            <span className={`import-report-action ${importReportActionClass(entry.action)}`}>
                              {importReportActionLabel(entry.action)}
                            </span>
                          </td>
                          <td>{entry.email || '—'}</td>
                          <td>{entry.username || '—'}</td>
                          <td>{[entry.first_name, entry.last_name].filter(Boolean).join(' ') || '—'}</td>
                          <td>{entry.phone || '—'}</td>
                          <td className="users-import-report-details">{entry.message || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowImportReportModal(false)}
              >
                Close
              </button>
              {importReport.reportCsv && (
                <button
                  className="save-btn"
                  onClick={() => downloadImportReport(importReport.reportCsv, importReport.summary?.completedAt)}
                >
                  Download report (CSV)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mass Email Modal */}
      {showMassEmailModal && (
        <div className="modal-overlay" onClick={() => !isSendingEmail && setShowMassEmailModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>📧 Send Mass Email</h3>
              <button className="close-btn" onClick={() => !isSendingEmail && setShowMassEmailModal(false)} disabled={isSendingEmail}>×</button>
            </div>
            
            <div className="modal-body">
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Use Template (Optional)
                </label>
                <select
                  value={massEmailForm.template_id}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px' }}
                  disabled={isSendingEmail}
                >
                  <option value="">-- Select Template --</option>
                  {emailTemplates.map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
              </div>

              {/* Template-specific fields */}
              {selectedTemplate && massEmailForm.template_variables && Object.keys(massEmailForm.template_variables).length > 0 && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#2a4a6a', borderRadius: '4px', border: '2px solid #61dafb' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--color-primary)' }}>📝 Customize Template Content</h4>
                  <p style={{ color: '#bbb', fontSize: '0.9rem', marginBottom: '1rem' }}>
                    Fill in the fields below to customize your email. These values will be automatically inserted into the template.
                  </p>
                  
                  {Object.entries(massEmailForm.template_variables).map(([key, value]) => (
                    <div key={key} style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#fff' }}>
                        {getFieldLabel(key)}
                      </label>
                      {/* Product selector for product_name field */}
                      {key === 'product_name' && products.length > 0 ? (
                        <select
                          value={products.find(p => p.name === value)?.id || ''}
                          onChange={(e) => {
                            const productId = e.target.value;
                            if (productId) {
                              handleProductSelect(productId);
                            } else {
                              handleTemplateVariableChange(key, '');
                            }
                          }}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', background: '#fff', color: '#333' }}
                          disabled={isSendingEmail}
                        >
                          <option value="">-- Select a Product --</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} {product.sku ? `(${product.sku})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : key.includes('content') || key.includes('message') || key.includes('description') || key.includes('intro') ? (
                        <textarea
                          value={value || ''}
                          onChange={(e) => handleTemplateVariableChange(key, e.target.value)}
                          placeholder={`Enter ${getFieldLabel(key).toLowerCase()}...`}
                          rows="3"
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', fontFamily: 'inherit' }}
                          disabled={isSendingEmail}
                        />
                      ) : (
                        <input
                          type="text"
                          value={value || ''}
                          onChange={(e) => handleTemplateVariableChange(key, e.target.value)}
                          placeholder={`Enter ${getFieldLabel(key).toLowerCase()}...`}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px' }}
                          disabled={isSendingEmail}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Subject <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <input
                  type="text"
                  value={massEmailForm.subject}
                  onChange={(e) => setMassEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject line"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px' }}
                  disabled={isSendingEmail}
                />
                <small style={{ color: '#888', fontSize: '0.85rem' }}>
                  Available variables: {'{{first_name}}'}, {'{{last_name}}'}, {'{{username}}'}, {'{{company_name}}'}
                </small>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  HTML Content <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <textarea
                  value={massEmailForm.html_content}
                  onChange={(e) => setMassEmailForm(prev => ({ ...prev, html_content: e.target.value }))}
                  placeholder="HTML email content..."
                  rows="12"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', fontFamily: 'monospace' }}
                  disabled={isSendingEmail}
                />
                <small style={{ color: '#888', fontSize: '0.85rem' }}>
                  Available variables: {'{{first_name}}'}, {'{{last_name}}'}, {'{{username}}'}, {'{{company_name}}'}
                </small>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Text Content (Optional - for plain text fallback)
                </label>
                <textarea
                  value={massEmailForm.text_content}
                  onChange={(e) => setMassEmailForm(prev => ({ ...prev, text_content: e.target.value }))}
                  placeholder="Plain text version..."
                  rows="6"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', fontFamily: 'monospace' }}
                  disabled={isSendingEmail}
                />
              </div>

              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#2a2a2a', borderRadius: '4px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>Recipient Filters</h4>
                <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={massEmailForm.recipient_filters.newsletter_subscribed}
                    onChange={(e) => setMassEmailForm(prev => ({
                      ...prev,
                      recipient_filters: { ...prev.recipient_filters, newsletter_subscribed: e.target.checked }
                    }))}
                    disabled={isSendingEmail}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span>Only newsletter subscribers</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={massEmailForm.recipient_filters.email_verified}
                    onChange={(e) => setMassEmailForm(prev => ({
                      ...prev,
                      recipient_filters: { ...prev.recipient_filters, email_verified: e.target.checked }
                    }))}
                    disabled={isSendingEmail}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span>Only verified email addresses</span>
                </label>
                <div style={{ marginTop: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>Filter by Role (Optional)</label>
                  <select
                    value={massEmailForm.recipient_filters.role}
                    onChange={(e) => setMassEmailForm(prev => ({
                      ...prev,
                      recipient_filters: { ...prev.recipient_filters, role: e.target.value }
                    }))}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px' }}
                    disabled={isSendingEmail}
                  >
                    <option value="">All Roles</option>
                    {roles.map(role => (
                      <option key={role.name} value={role.name}>{role.display_name || role.name}</option>
                    ))}
                  </select>
                </div>
                {selectedUsers.length > 0 && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--color-background)', borderRadius: '4px' }}>
                    <strong>📌 {selectedUsers.length} selected user(s) will receive this email</strong>
                    <br />
                    <small>If no filters are selected, all active users will receive the email</small>
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => setShowMassEmailModal(false)}
                disabled={isSendingEmail}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={handleSendMassEmail}
                disabled={isSendingEmail}
              >
                {isSendingEmail ? '⏳ Sending...' : '📧 Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Templates Modal */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>📝 Email Templates</h3>
              <button className="close-btn" onClick={() => setShowTemplateModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <h4 style={{ marginTop: 0 }}>Templates</h4>
                  <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '4px', padding: '0.5rem' }}>
                    {emailTemplates.length === 0 ? (
                      <p style={{ color: '#888', textAlign: 'center', padding: '1rem' }}>No templates yet</p>
                    ) : (
                      emailTemplates.map(template => (
                        <div key={template.id} style={{ 
                          padding: '0.75rem', 
                          marginBottom: '0.5rem', 
                          background: templateForm.id === template.id ? '#2a4a6a' : '#2a2a2a',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          border: templateForm.id === template.id ? '2px solid #61dafb' : '1px solid #444'
                        }}
                        onClick={() => handleEditTemplate(template)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{template.name}</strong>
                              <small style={{ color: '#aaa', fontSize: '0.85rem' }}>{template.subject}</small>
                            </div>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTestTemplate(template.id);
                                }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#4a4a4a', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                title="Test template"
                              >
                                ✉️
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTemplate(template.id);
                                }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#6a2a2a', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                title="Delete template"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setTemplateForm({
                        id: null,
                        name: '',
                        subject: '',
                        html_content: '',
                        text_content: '',
                        variables: {}
                      });
                    }}
                    style={{ width: '100%', marginTop: '0.5rem', padding: '0.5rem', background: '#2a4a6a', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    ➕ New Template
                  </button>
                  <button
                    onClick={handleInsertExampleTemplates}
                    style={{ width: '100%', marginTop: '0.5rem', padding: '0.5rem', background: '#16a34a', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', color: 'white' }}
                    title="Insert 5 example email templates"
                  >
                    📥 Insert Example Templates
                  </button>
                </div>

                <div>
                  <h4 style={{ marginTop: 0 }}>Template Editor</h4>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Template Name <span style={{ color: 'var(--color-error)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Announcement Template"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px' }}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Subject <span style={{ color: 'var(--color-error)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Email subject"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px' }}
                    />
                    <small style={{ color: '#888', fontSize: '0.85rem' }}>
                      Variables: {'{{first_name}}'}, {'{{last_name}}'}, {'{{username}}'}, {'{{company_name}}'}
                    </small>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                      HTML Content <span style={{ color: 'var(--color-error)' }}>*</span>
                    </label>
                    <textarea
                      value={templateForm.html_content}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, html_content: e.target.value }))}
                      placeholder="HTML email content..."
                      rows="10"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', fontFamily: 'monospace' }}
                    />
                    <small style={{ color: '#888', fontSize: '0.85rem' }}>
                      Variables: {'{{first_name}}'}, {'{{last_name}}'}, {'{{username}}'}, {'{{company_name}}'}
                    </small>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Text Content (Optional)
                    </label>
                    <textarea
                      value={templateForm.text_content}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, text_content: e.target.value }))}
                      placeholder="Plain text version..."
                      rows="6"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', fontFamily: 'monospace' }}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => setShowTemplateModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={handleSaveTemplate}
              >
                💾 Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Actions Modal */}
      {showUserActionsModal && selectedUserForActions && (
        <div className="modal-overlay" onClick={() => setShowUserActionsModal(false)}>
          <div className="modal-content user-actions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>👤 User Actions</h3>
              <button className="close-btn" onClick={() => setShowUserActionsModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="user-actions-info">
                <div className="user-avatar" style={{ width: '48px', height: '48px', fontSize: '1.5rem' }}>
                  {selectedUserForActions.first_name ? selectedUserForActions.first_name.charAt(0) : selectedUserForActions.username.charAt(0)}
                </div>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#e0e0e0' }}>
                    {selectedUserForActions.first_name && selectedUserForActions.last_name 
                      ? `${selectedUserForActions.first_name} ${selectedUserForActions.last_name}`
                      : selectedUserForActions.username
                    }
                  </h4>
                  <p style={{ margin: '0', color: '#888', fontSize: '0.9rem' }}>@{selectedUserForActions.username}</p>
                  <p style={{ margin: '0.25rem 0 0 0', color: '#aaa', fontSize: '0.85rem' }}>{selectedUserForActions.email}</p>
                </div>
              </div>

              <div className="user-actions-buttons">
                <button 
                  className="action-modal-btn edit-btn"
                  onClick={handleQuickEdit}
                >
                  <span className="action-icon">✏️</span>
                  <div className="action-text">
                    <strong>Edit Profile</strong>
                    <small>Update user information</small>
                  </div>
                </button>

                <button 
                  className="action-modal-btn password-btn"
                  onClick={handleQuickPassword}
                >
                  <span className="action-icon">🔑</span>
                  <div className="action-text">
                    <strong>Reset Password</strong>
                    <small>Change user password</small>
                  </div>
                </button>

                <button 
                  className="action-modal-btn manage-roles-btn"
                  onClick={handleQuickRoles}
                >
                  <span className="action-icon">👥</span>
                  <div className="action-text">
                    <strong>Manage Roles</strong>
                    <small>Assign or remove roles</small>
                  </div>
                </button>

                <button
                  className="action-modal-btn activity-btn"
                  onClick={handleQuickActivity}
                >
                  <span className="action-icon">📋</span>
                  <div className="action-text">
                    <strong>View Activity</strong>
                    <small>
                      {selectedUserForActions.last_seen_at
                        ? `Last seen ${formatDateTime(selectedUserForActions.last_seen_at)}`
                        : 'Pages, products, searches, logins'}
                    </small>
                  </div>
                </button>

                <button 
                  className="action-modal-btn delete-btn"
                  onClick={handleQuickDelete}
                  disabled={selectedUserForActions.source === 'demo'}
                >
                  <span className="action-icon">🗑️</span>
                  <div className="action-text">
                    <strong>Delete User</strong>
                    <small>{selectedUserForActions.source === 'demo' ? 'Demo users cannot be deleted' : 'Permanently remove user'}</small>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Activity Modal */}
      {showActivityModal && activityUser && (
        <div className="modal-overlay" onClick={() => setShowActivityModal(false)}>
          <div
            className="modal-content user-activity-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '720px', width: '95%' }}
          >
            <div className="modal-header">
              <h3>📋 Activity — @{activityUser.username || activityUser.email}</h3>
              <button type="button" className="close-btn" onClick={() => setShowActivityModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ margin: '0 0 0.75rem', color: '#aaa', fontSize: '0.9rem' }}>
                {activityUser.email}
                {activityUser.last_seen_at || activityUser.lastSeenAt
                  ? ` · Last seen ${formatDateTime(activityUser.last_seen_at || activityUser.lastSeenAt)}`
                  : ''}
                {activityTotal ? ` · ${activityTotal} event${activityTotal === 1 ? '' : 's'}` : ''}
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
                {['all', 'login', 'page_view', 'product_view', 'add_to_cart', 'search', 'register'].map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`btn btn-sm ${activityFilter === f ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => openActivityModal(activityUser, f)}
                  >
                    {f === 'all' ? 'All' : f.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>

              {activityLoading && <p>Loading activity…</p>}
              {activityError && <p style={{ color: '#f88' }}>{activityError}</p>}
              {!activityLoading && !activityError && activityEvents.length === 0 && (
                <p style={{ color: '#888' }}>
                  No activity recorded yet. Events appear after the user logs in and browses while signed in.
                </p>
              )}

              {!activityLoading && activityEvents.length > 0 && (
                <div className="user-activity-list" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                  <table className="users-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Event</th>
                        <th>Path / detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityEvents.map((ev) => (
                        <tr key={ev.id}>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                            {formatDateTime(ev.createdAt)}
                          </td>
                          <td>
                            <span className="role-badge" style={{ fontSize: '0.75rem' }}>
                              {ev.eventType}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.85rem' }}>
                            <div>{ev.path || '—'}</div>
                            {formatActivityMeta(ev.meta) && (
                              <div style={{ color: '#888', marginTop: '0.15rem' }}>
                                {formatActivityMeta(ev.meta)}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowActivityModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
